// The bucket a rule measures over.
//
// Four kinds, in the vocabulary the backend stores: the whole account, a list
// of individual symbols, one asset class, or one named group. The wording
// below is load-bearing, not decoration — a list of symbols is one INDEPENDENT
// bucket per symbol, and a group is one aggregate bucket. An author who reads
// "5 lots on EURUSD, XAUUSD" as a shared budget has authored the wrong product.

import { Show } from 'solid-js';
import {
  ASSET_CLASSES,
  SCOPE_KINDS,
  describeScope,
  type RuleScope,
  type SymbolGroupScope,
} from '../core/ruleRegistry';
import Dropdown from './Dropdown';

interface ScopePickerProps {
  scope: RuleScope;
  /** The unit the limit is authored in, shown beside the bucket so the two
   *  are read together ("5 lots, on each of EURUSD and XAUUSD"). */
  units: string;
  onChange: (scope: RuleScope) => void;
}

/** The canonical execution form: upper case, no blanks, no duplicates, sorted. */
function parseSymbols(raw: string): string[] {
  return [
    ...new Set(
      raw
        .split(/[,\s]+/)
        .map((symbol) => symbol.trim().toUpperCase())
        .filter((symbol) => symbol.length > 0),
    ),
  ].sort();
}

export default function ScopePicker(props: ScopePickerProps) {
  function changeKind(kind: string) {
    switch (kind) {
      case 'symbols':
        props.onChange({ kind: 'symbols', value: [] });
        return;
      case 'asset_class':
        props.onChange({ kind: 'asset_class', value: ASSET_CLASSES[0] });
        return;
      case 'symbol_group':
        props.onChange({ kind: 'symbol_group', value: { id: '', revision: 1, members: [] } });
        return;
      default:
        props.onChange({ kind: 'account' });
    }
  }

  const symbols = () => (props.scope.kind === 'symbols' ? props.scope.value : []);
  const group = (): SymbolGroupScope =>
    props.scope.kind === 'symbol_group'
      ? props.scope.value
      : { id: '', revision: 1, members: [] };
  /** One field at a time; the other two are carried unchanged. */
  function changeGroup(partial: Partial<SymbolGroupScope>) {
    props.onChange({ kind: 'symbol_group', value: { ...group(), ...partial } });
  }

  return (
    <div class="ch-rule-scope">
      <div class="ch-field ch-field-sm">
        <span class="ch-label">Measured over</span>
        <Dropdown
          value={props.scope.kind}
          options={SCOPE_KINDS.map((k) => ({ value: k.value, label: k.label }))}
          onChange={changeKind}
        />
      </div>

      <Show when={props.scope.kind === 'symbols'}>
        <label class="ch-field">
          Symbols
          <input
            class="ch-input"
            aria-label="Scope symbols"
            placeholder="EURUSD, XAUUSD"
            value={symbols().join(', ')}
            onChange={(e) =>
              props.onChange({ kind: 'symbols', value: parseSymbols(e.currentTarget.value) })
            }
          />
        </label>
      </Show>

      <Show when={props.scope.kind === 'asset_class'}>
        <div class="ch-field ch-field-sm">
          <span class="ch-label">Asset class</span>
          <Dropdown
            value={props.scope.kind === 'asset_class' ? props.scope.value : ASSET_CLASSES[0]}
            options={ASSET_CLASSES.map((c) => ({ value: c, label: c }))}
            onChange={(v) => props.onChange({ kind: 'asset_class', value: v })}
          />
        </div>
      </Show>

      <Show when={props.scope.kind === 'symbol_group'}>
        <label class="ch-field">
          Group id
          <input
            class="ch-input"
            aria-label="Scope group id"
            placeholder="00000000-0000-0000-0000-000000000000"
            value={group().id}
            onChange={(e) => changeGroup({ id: e.currentTarget.value.trim() })}
          />
        </label>
        {/* The MEMBERS. A group id is a name; the membership is what the limit
            actually measures, and a revision pins which membership was sold.
            Publication refuses a group whose members are not pinned, so this is
            authored here rather than discovered at publish time. */}
        <label class="ch-field">
          Members
          <input
            class="ch-input"
            aria-label="Scope group members"
            placeholder="EURUSD, GBPUSD"
            value={group().members.join(', ')}
            onChange={(e) => changeGroup({ members: parseSymbols(e.currentTarget.value) })}
          />
        </label>
        <label class="ch-field ch-field-sm">
          Revision
          <input
            class="ch-input"
            aria-label="Scope group revision"
            type="number"
            min="1"
            step="1"
            value={String(group().revision)}
            onChange={(e) => changeGroup({ revision: Number(e.currentTarget.value) || 1 })}
          />
        </label>
        <p class="ch-rule-scope-note">
          {group().members.length === 0
            ? 'Resolves to nothing yet.'
            : `Resolves to ${group().members.length} instrument${group().members.length === 1 ? '' : 's'}: ${group().members.join(', ')}.`}
        </p>
      </Show>

      <p class="ch-rule-scope-note">
        {describeScope(props.scope)} Limit is in {props.units}.
      </p>
    </div>
  );
}
